import type { CampLocation } from "@/types/campLocation";
import type { Offer } from "@/types/offer";

const STOPWORDS = new Set(
  [
    // ultra-common
    "the", "and", "or", "a", "an", "to", "of", "in", "on", "for", "with", "at", "by", "from", "as",
    "is", "are", "be", "being", "been", "this", "that", "these", "those", "it", "its", "your", "you",
    "we", "our", "ours", "us", "they", "their", "them", "i", "me", "my", "mine",
    "will", "can", "could", "should", "would", "may", "might", "must", "able",
    "join", "joined", "joining", "include", "includes", "including", "featuring", "feature",
    "enjoy", "enjoying", "discover", "discovering", "experience", "experienced", "hands", "hands-on",
    "great", "best", "new", "all", "each", "every",
    // camp-generic
    "camp", "camps", "kids", "kid", "child", "children", "summer", "day", "week", "weeks", "session", "sessions",
    "program", "programs", "activities", "activity", "fun", "learn", "learning", "explore", "exploring",
    "build", "building", "create", "creating", "make", "making", "play", "playing",
    // common schedule/format words that hurt search quality
    "full", "half", "daytime", "morning", "afternoon", "evening", "overnight", "weekend",
    "ages", "age", "grade", "grades", "year", "years", "old",
    // scheduling/generic nouns
    "am", "pm", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
    "june", "july", "august", "september", "october", "november", "december", "jan", "feb", "mar", "apr", "may",
  ]
);

function normalizeText(s: string): string {
  return String(s || "")
    .replace(/https?:\/\/\S+/g, " ")
    .replace(/[^a-zA-Z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenize(s: string): string[] {
  const raw = normalizeText(s);
  if (!raw) return [];
  return raw
    .split(" ")
    .map((w) => w.trim().toLowerCase())
    .filter(Boolean)
    .map((w) => w.replace(/^-+|-+$/g, ""));
}

function isGoodToken(t: string, banned: Set<string>): boolean {
  if (!t) return false;
  if (t.length < 3) return false;
  if (STOPWORDS.has(t)) return false;
  if (banned.has(t)) return false;
  // Exclude anything with digits (age ranges like 00-4, 5-7, 2026, etc.)
  if (/\d/.test(t)) return false;
  if (/^(st|nd|rd|th)$/.test(t)) return false;
  return true;
}

function ngrams(tokens: string[], n: 2 | 3): string[] {
  const out: string[] = [];
  for (let i = 0; i + n - 1 < tokens.length; i++) {
    out.push(tokens.slice(i, i + n).join(" "));
  }
  return out;
}

function isGoodPhrase(phrase: string, banned: Set<string>): boolean {
  const parts = phrase.split(" ").filter(Boolean);
  if (parts.length < 2) return false;
  return parts.every((p) => isGoodToken(p, banned));
}

/**
 * Pick distinctive activity-ish keywords from offers (session titles + descriptions).
 * Uses a simple TF * (1/DF)^alpha scoring so "chess" or "lego" can surface even if not everywhere.
 */
function extractDistinctiveKeywords(params: {
  location: CampLocation;
  offers: Offer[];
  maxKeywords: number;
}): string[] {
  const { location, offers, maxKeywords } = params;
  const banned = new Set<string>();
  for (const t of tokenize(location?.name || "")) banned.add(t);
  for (const t of tokenize(location?.address?.city || "")) banned.add(t);
  for (const t of tokenize(location?.address?.state || "")) banned.add(t);

  const tf = new Map<string, number>();
  const df = new Map<string, number>();

  // Only use offer fields we trust for this purpose:
  // - offers/<id>/displayTitle
  // - offers/<id>/description
  // Ignore placeholder offers entirely.
  const offerDocs = (offers || [])
    .filter((o: any) => o?.isPlaceholder !== true)
    .map((o) => {
      const title = normalizeText(String(o?.displayTitle || ""));
      const desc = normalizeText(String(o?.description || ""));
      return { title, body: desc };
    });

  for (const d of offerDocs) {
    const titleTokens = tokenize(d.title).filter((t) => isGoodToken(t, banned));
    const bodyTokens = tokenize(d.body).filter((t) => isGoodToken(t, banned));
    const tokens = [...titleTokens, ...bodyTokens];
    const seen = new Set<string>();

    // Weight title tokens higher (more likely to contain the actual activity name)
    for (const t of titleTokens) {
      tf.set(t, (tf.get(t) || 0) + 3);
      seen.add(t);
    }
    for (const t of bodyTokens) {
      tf.set(t, (tf.get(t) || 0) + 1);
      seen.add(t);
    }

    // Add strong multi-word phrases from titles (e.g. "lego robotics", "rock climbing", "martial arts")
    const titleBigrams = ngrams(titleTokens, 2).filter((p) => isGoodPhrase(p, banned));
    const titleTrigrams = ngrams(titleTokens, 3).filter((p) => isGoodPhrase(p, banned));
    for (const p of [...titleBigrams, ...titleTrigrams]) {
      tf.set(p, (tf.get(p) || 0) + 5);
      seen.add(p);
    }
    for (const t of seen) df.set(t, (df.get(t) || 0) + 1);
  }

  const alpha = 0.7;
  const scored = Array.from(tf.entries()).map(([term, count]) => {
    const d = df.get(term) || 1;
    const score = count * Math.pow(1 / d, alpha);
    return { term, score, tf: count, df: d };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, Math.max(0, maxKeywords)).map((x) => x.term);
}

function titleCaseKeyword(s: string): string {
  // Keep acronyms like LEGO uppercase-ish; otherwise Title Case words.
  return s
    .split(" ")
    .filter(Boolean)
    .map((w) => {
      if (w.toUpperCase() === w && w.length <= 5) return w; // acronym
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(" ");
}

/**
 * Build a list of suggested keywords derived ONLY from offers' displayTitle/description
 * (placeholders filtered out), suitable for showing under the query inputs.
 */
export function buildSuggestedOfferKeywords(params: { location: CampLocation; offers: Offer[]; maxKeywords?: number }): string[] {
  const { location, offers } = params;
  const maxKeywords = Math.max(0, Math.min(30, Number(params.maxKeywords) || 30));
  const raw = extractDistinctiveKeywords({ location, offers, maxKeywords });
  // Normalize + Title Case for UI chips.
  const out = raw.map(titleCaseKeyword);
  return Array.from(new Set(out));
}

/**
 * Build up to 3 seeded Unsplash queries from location content + offers.
 * Keep them human-ish (not internal keys) so results are good.
 */
export function buildUnsplashSeedQueries(params: { location: CampLocation; offers: Offer[] }): string[] {
  const { location, offers } = params;
  const name = String(location?.name || "").trim();
  const city = String(location?.address?.city || "").trim();
  const state = String(location?.address?.state || "").trim();

  const place = [city, state].filter(Boolean).join(" ");

  // Pull distinctive terms from ALL offers (sessions) at this location.
  const keywords = extractDistinctiveKeywords({ location, offers, maxKeywords: 6 });
  // Prefer phrases (contain space) first for better Unsplash recall.
  const phrase1 = keywords.find((k) => k.includes(" ")) || null;
  const phrase2 = keywords.filter((k) => k.includes(" "))[1] || null;
  const token1 = keywords.find((k) => !k.includes(" ")) || null;
  const token2 = keywords.filter((k) => !k.includes(" "))[1] || null;
  const k1 = phrase1 || token1 || "hiking";
  const k2 = phrase2 || token2 || "outdoor";

  const q1 = [name, place, "kids camp"].filter(Boolean).join(" ").trim();
  // Keep these “nouny” (avoid verb-like glue words).
  const q2 = ["kids", k1, "camp"].filter(Boolean).join(" ").trim();
  const q3 = ["kids", k2, "camp"].filter(Boolean).join(" ").trim();

  return [q1, q2, q3].filter(Boolean);
}

